import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../api/axios';
import { VERSION_INFO } from '../version';

/** Sum credits from tree modules — correct when API summary row is stale (e.g. 50/45/30). */
function summaryFromTreeModules(tree) {
  const sumList = (list) =>
    (list || []).reduce((s, m) => s + (Number(m?.credits) || 0), 0);
  return {
    knowledge_based_total: sumList(tree?.KM),
    practical_requirements_total: sumList(tree?.PM),
    workplace_experience_total: sumList(tree?.WM),
  };
}

function summariesDiffer(a, b) {
  if (!a || !b) return true;
  return (
    Number(a.knowledge_based_total) !== Number(b.knowledge_based_total) ||
    Number(a.practical_requirements_total) !== Number(b.practical_requirements_total) ||
    Number(a.workplace_experience_total) !== Number(b.workplace_experience_total)
  );
}

// Color scheme for different categories
const categoryColors = {
  knowledge_based: {
    primary: '#1976d2',
    light: '#e3f2fd',
    dark: '#1565c0'
  },
  practical_requirements: {
    primary: '#388e3c',
    light: '#e8f5e8',
    dark: '#2e7d32'
  },
  workplace_experience: {
    primary: '#f57c00',
    light: '#fff3e0',
    dark: '#ef6c00'
  },
  assessment: {
    primary: '#7b1fa2',
    light: '#f3e5f5',
    dark: '#6a1b9a'
  }
};

// Category icons
const categoryIcons = {
  knowledge_based: SchoolIcon,
  practical_requirements: BuildIcon,
  workplace_experience: WorkIcon,
  assessment: AssessmentIcon
};

// Category labels
const categoryLabels = {
  knowledge_based: 'Knowledge Modules',
  practical_requirements: 'Practical Requirements',
  workplace_experience: 'Workplace Experience',
  assessment: 'Assessment'
};

export default function CurriculumTreeView({ qualificationId, curriculumSummary, onDebugGoCanvas, onDebugRegenerateELOs, hasELOs = false, onOpenCanvas, nodes: nodesExternal, onExtractionStateChange, extractNonce = 0 }) {

  
  // Clean up module titles by removing dots and extra formatting
  const cleanTitle = (title) => {
    if (!title) return '';
    return title
      .replace(/\.{3,}/g, '') // Remove sequences of 3+ dots
      .replace(/[.\s]+$/g, '') // Remove trailing dots and spaces
      .replace(/^[.\s]+/g, '') // Remove leading dots and spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  };
  const [expandedCategories, setExpandedCategories] = useState({
    knowledge_based: false,
    practical_requirements: false,
    workplace_experience: false,
    assessment: false
  });
  const [expandedModules, setExpandedModules] = useState({});
  const [nodes, setNodes] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const esRef = useRef(null);
  const [targetCredits, setTargetCredits] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);
  const [debugEvents, setDebugEvents] = useState([]);
  const [targetNodes, setTargetNodes] = useState(null);
  const [isCounting, setIsCounting] = useState(false);
  const [countError, setCountError] = useState(null);
  const [localTree, setLocalTree] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [debugOpen, setDebugOpen] = useState(false);
  const [splitPos, setSplitPos] = useState(45); // percentage for left pane width
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [extractionIssues, setExtractionIssues] = useState([]);
  const [codeStatistics, setCodeStatistics] = useState(null);
  const [issuesDialogOpen, setIssuesDialogOpen] = useState(false);
  const [newCurriculumData, setNewCurriculumData] = useState(null);
  const [newCurriculumSummary, setNewCurriculumSummary] = useState(null);
  const [lastExtractSummary, setLastExtractSummary] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const splitContainerRef = useRef(null);
  const showDebug = false;

  const notifyExtractionState = (tree) => {
    if (typeof onExtractionStateChange !== 'function') return;
    const extracted = Boolean(tree && ((tree.KM || []).length + (tree.PM || []).length + (tree.WM || []).length > 0));
    onExtractionStateChange({ extracted });
  };

  // First extraction only. Later clicks must not wipe persisted numbers/structure.
  const handleRegenerate = async () => {
    if (newCurriculumData && ((newCurriculumData.KM || []).length + (newCurriculumData.PM || []).length + (newCurriculumData.WM || []).length > 0)) {
      return;
    }
    setIsRegenerating(true);
    try {
      console.log('[CurriculumDoc] tree:extract:start', {
        qualificationId,
        at: new Date().toISOString(),
      });
      const response = await api.post(`/qualifications/${qualificationId}/curriculum/extract/new`);
      console.log('[CurriculumDoc] tree:extract:response', {
        qualificationId,
        success: response.data?.success,
        alreadyExtracted: response.data?.alreadyExtracted,
        debug: response.data?.debug,
        parsedMeta: response.data?.parsedMeta,
      });
      
      if (response.data.success) {
        let extractSummary = null;
        const parsed = response.data.parsedMeta;
        if (parsed) {
          extractSummary = {
            knowledge_based_total: parsed.km_credits ?? 0,
            practical_requirements_total: parsed.pm_credits ?? 0,
            workplace_experience_total: parsed.wm_credits ?? 0,
          };
          setLastExtractSummary(extractSummary);
          setNewCurriculumSummary(extractSummary);
          console.log('[CurriculumDoc] tree:usingExtractSummary', { qualificationId, extractSummary });
        }
        setExtractionIssues([]);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await fetchNewCurriculumData({ extractSummary });
        await fetchExtractionIssues();
        await fetchCodeStatistics();
        
        setRefreshCounter(prev => prev + 1);
      } else {
        alert('Extraction failed. Please try again.');
      }
    } catch (error) {
      alert('Failed to extract curriculum. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Function to fetch extraction issues
  const fetchExtractionIssues = async () => {
    try {

      const response = await api.get(`/qualifications/${qualificationId}/issues`);

      
      // Deduplicate issues by creating a unique key from type + code + message
      const issuesArray = Array.isArray(response.data) ? response.data : [];
      const seen = new Set();
      const dedupedIssues = issuesArray.filter(issue => {
        // Create unique key from issue properties
        const key = `${issue.severity || issue.s}-${issue.node || issue.code}-${issue.message || issue.m}`;
        if (seen.has(key)) {
          return false; // Skip duplicate
        }
        seen.add(key);
        return true;
      });
      
      // Normalize issue format (handle both old and new formats)
      const normalizedIssues = dedupedIssues.map(issue => ({
        severity: issue.severity || issue.s || 'WARN',
        node: issue.node || issue.code,
        message: issue.message || issue.m,
        parent: issue.parent || null
      }));
      
      setExtractionIssues(normalizedIssues);
    } catch (error) {

    }
  };

  // Function to fetch code statistics
  const fetchCodeStatistics = async () => {
    try {

      const response = await api.get(`/qualifications/${qualificationId}/code-statistics`);

      setCodeStatistics(response.data.codeStatistics);
    } catch (error) {

    }
  };

  // Function to fetch new curriculum data
  const fetchNewCurriculumData = async (options = {}) => {
    try {

      const response = await api.get(`/qualifications/${qualificationId}/curriculum-tree/local?t=${Date.now()}`);

      if (response.data && response.data.tree) {
        const tree = response.data.tree;
        const kmCount = (tree.KM || []).length;
        const pmCount = (tree.PM || []).length;
        const wmCount = (tree.WM || []).length;
        const treeSummary = response.data.summary;
        const debug = response.data.debug;
        const moduleSummary = summaryFromTreeModules(tree);
        const pipelineHeader = response.headers?.['x-curriculum-pipeline'];
        const gitHeader = response.headers?.['x-git-commit'];
        console.log('[CurriculumDoc] tree:loaded', {
          qualificationId,
          summary: treeSummary,
          moduleSummary,
          debug,
          pipelineHeader,
          gitHeader,
          moduleCounts: { km: kmCount, pm: pmCount, wm: wmCount },
        });
        setNewCurriculumData(tree);
        notifyExtractionState(tree);
        const extractSummary = options.extractSummary || null;
        const persistedTotal = treeSummary
          ? (Number(treeSummary.knowledge_based_total) || 0) +
            (Number(treeSummary.practical_requirements_total) || 0) +
            (Number(treeSummary.workplace_experience_total) || 0)
          : 0;
        const extractTotal = extractSummary
          ? (Number(extractSummary.knowledge_based_total) || 0) +
            (Number(extractSummary.practical_requirements_total) || 0) +
            (Number(extractSummary.workplace_experience_total) || 0)
          : 0;
        // Keep first-extraction totals; only fall back to module sums when none were persisted.
        let effectiveSummary = treeSummary || extractSummary || moduleSummary;
        if (persistedTotal > 0) {
          effectiveSummary = treeSummary;
        } else if (extractTotal > 0) {
          effectiveSummary = extractSummary;
        } else if (moduleSummary) {
          effectiveSummary = moduleSummary;
        }
        if (treeSummary && persistedTotal > 0 && summariesDiffer(moduleSummary, treeSummary)) {
          console.log('[CurriculumDoc] tree:usingPersistedSummary', {
            qualificationId,
            treeSummary,
            moduleSummary,
            debug,
          });
        }
        setNewCurriculumSummary(effectiveSummary);
      } else {
        console.log('[CurriculumDoc] tree:empty', { qualificationId });
        setNewCurriculumData(null);
        setNewCurriculumSummary(null);
        notifyExtractionState(null);
      }
    } catch (error) {
      console.error('[CurriculumDoc] tree:fetchFailed', {
        qualificationId,
        message: error.response?.data?.message || error.message,
      });
      setNewCurriculumData(null);
      notifyExtractionState(null);
    }
  };

  // Verify deployed frontend/backend versions match (detect stale production)
  useEffect(() => {
    if (!qualificationId) return;
    (async () => {
      try {
        const res = await api.get('/version');
        const server = res.data || {};
        const pipelineOk = server.curriculum?.curriculumPipelineMatch === true;
        const commitMatch = VERSION_INFO?.commit && server.gitCommit && VERSION_INFO.commit === server.gitCommit;
        console.log('[CurriculumDoc] deploy:check', {
          qualificationId,
          frontendCommit: VERSION_INFO?.commit,
          frontendBuildDate: VERSION_INFO?.buildDate,
          backendCommit: server.gitCommit,
          curriculumPipeline: server.buildInfo?.curriculumPipeline,
          pipelineOk,
          commitMatch,
          useS3: server.curriculum?.useS3,
          serverMessage: server.message,
        });
        if (!pipelineOk) {
          console.error(
            '[CurriculumDoc] STALE BACKEND — expected curriculumPipeline v4-s3-scoped-qual-normalize. Git pull, npm run build, pm2 restart on server.'
          );
        }
        if (VERSION_INFO?.commit && server.gitCommit && !commitMatch) {
          console.warn('[CurriculumDoc] commit mismatch (frontend static bundle vs API)', {
            frontend: VERSION_INFO.commit,
            backend: server.gitCommit,
          });
        }
      } catch (e) {
        console.error('[CurriculumDoc] deploy:check failed', e.response?.data || e.message);
      }
    })();
  }, [qualificationId]);

  // Fetch issues and curriculum data on component mount and refresh
  useEffect(() => {
    if (qualificationId) {
      fetchExtractionIssues();
      fetchNewCurriculumData();
      fetchCodeStatistics();
    }
  }, [qualificationId, refreshCounter, extractNonce]);

  // Always fetch nodes on mount/update; if parent supplies nodes, mirror them first
  useEffect(() => {
    if (!qualificationId) return;
    if (Array.isArray(nodesExternal)) {
      setNodes(nodesExternal);
      return; // Don't fetch internally if external nodes are provided
    }
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
        if (mounted && res.data && Array.isArray(res.data.nodes)) setNodes(res.data.nodes);
      } catch {}
    })();
    return () => { mounted = false; };
  }, [qualificationId, nodesExternal]);

  // If parent supplies nodes, prefer them
  const effectiveNodes = (Array.isArray(nodesExternal) && nodesExternal.length > 0) ? nodesExternal : nodes;


  // Extract PS and WE topics from newCurriculumData and merge with effectiveNodes
  const enrichedNodes = useMemo(() => {
    if (!newCurriculumData) {
      return effectiveNodes;
    }

    const additionalNodes = [];
    
    // Extract PS topics from PM modules (newCurriculumData.PM structure)
    if (newCurriculumData.PM && Array.isArray(newCurriculumData.PM)) {
      newCurriculumData.PM.forEach(module => {
        if (module.subtasks && Array.isArray(module.subtasks)) {
          module.subtasks.forEach(subtask => {
            if (subtask.code && subtask.code.includes('PS')) {
              additionalNodes.push({
                code: subtask.code,
                title: subtask.title,
                node_type: 'practical_skill',
                module_code: module.code,
                weight_pct: subtask.weight_pct || subtask.percent_weight || 0,
                credits: subtask.credits || 0,
                notional_hours: subtask.notional_hours || 0,
                text: subtask.title,
                approved: false
              });
            }
          });
        }
      });
    }
    
    // Extract WE topics from WM modules (newCurriculumData.WM structure)
    if (newCurriculumData.WM && Array.isArray(newCurriculumData.WM)) {
      newCurriculumData.WM.forEach(module => {
        if (module.subtasks && Array.isArray(module.subtasks)) {
          module.subtasks.forEach(subtask => {
            if (subtask.code && subtask.code.includes('WE')) {
              additionalNodes.push({
                code: subtask.code,
                title: subtask.title,
                node_type: 'work_experience_item',
                module_code: module.code,
                weight_pct: subtask.weight_pct || subtask.percent_weight || 0,
                credits: subtask.credits || 0,
                notional_hours: subtask.notional_hours || 0,
                text: subtask.title,
                approved: false
              });
            }
          });
        }
      });
    }

    // Merge with existing nodes, avoiding duplicates
    const existingCodes = new Set(effectiveNodes.map(n => n.code));
    const newNodes = additionalNodes.filter(n => !existingCodes.has(n.code));

    return [...effectiveNodes, ...newNodes];
  }, [effectiveNodes, newCurriculumData]);

  // Listen for global refresh events after commits and reload nodes
  useEffect(() => {
    if (!qualificationId) return;
    const handler = async (e) => {
      try {
        // If a qualificationId was specified, ensure it matches
        const wanted = e?.detail?.qualificationId;
        if (wanted && String(wanted) !== String(qualificationId)) return;
        // Only refetch if no external nodes are provided
        if (!Array.isArray(nodesExternal) || nodesExternal.length === 0) {
          const res = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
          if (res.data && Array.isArray(res.data.nodes)) setNodes(res.data.nodes);
        }
      } catch {}
    };
    window.addEventListener('nodes:refresh', handler);
    return () => window.removeEventListener('nodes:refresh', handler);
  }, [qualificationId, nodesExternal]);

  // Fetch target credits from qualification overview (Step 1 analysis)
  useEffect(() => {
    if (!qualificationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/qualifications/${qualificationId}`);
        const q = await res.json();
        let minCredits = 0;
        if (typeof q.minimum_credits === 'number') {
          minCredits = q.minimum_credits;
        } else if (q.ai_analysis) {
          let parsed = q.ai_analysis;
          if (typeof parsed === 'string') {
            const match = parsed.match(/```json\s*([\s\S]*?)\s*```/);
            if (match) { parsed = match[1]; }
            try { parsed = JSON.parse(parsed); } catch { parsed = null; }
          }
          if (parsed && typeof parsed === 'object') {
            const candidates = [
              parsed['MINIMUM CREDITS'],
              parsed['Minimum Credits'],
              parsed['minimum_credits'],
              parsed.overview?.credits,
              parsed.credits
            ].filter(v => v !== undefined && v !== null);
            if (candidates.length > 0) {
              const asNum = parseInt(String(candidates[0]).replace(/[^0-9]/g, ''), 10);
              if (!Number.isNaN(asNum)) minCredits = asNum;
            }
          }
        }
        if (!cancelled) setTargetCredits(minCredits || 0);
      } catch {
        if (!cancelled) setTargetCredits(0);
      }
    })();
    return () => { cancelled = true; };
  }, [qualificationId]);

  // Start/stop streaming extraction via SSE (backend writes nodes into DB; our poll will pick them up)
  const startExtraction = () => {
    if (!qualificationId || isStreaming) return;
    if (newCurriculumData && ((newCurriculumData.KM || []).length + (newCurriculumData.PM || []).length + (newCurriculumData.WM || []).length > 0)) {
      return;
    }
    const buildNodesFromLocalTree = (tree) => {
      const out = [];
      const parseCreditsFromText = (s) => {
        if (!s || typeof s !== 'string') return 0;
        const m = s.match(/credits?\s*[:\-]?\s*(\d{1,3})(?!\d)/i);
        return m ? parseInt(m[1], 10) : 0;
      };
      const push = (n) => out.push({
        node_type: n.node_type,
        code: n.code || '',
        title: n.title || n.code || '',
        text: n.text || '',
        is_mandatory: n.is_mandatory ?? !!n.code,
        module_code: n.module_code || '',
        parent_code: n.parent_code || '',
        weight_pct: n.weight_pct || 0,
        credits: n.credits || 0,
        nqf_level: n.nqf_level || 0,
        evidence_type: n.evidence_type || '',
        links: n.links || []
      });
      const ensureTitle = (code) => code || '';
      // KM
      (tree.KM || []).forEach(km => {
        const mod = km.code;
        const derivedCr = km.credits || parseCreditsFromText(km.description || km.title || '');
        push({ node_type:'module_km', code: mod, title: km.description || km.title || ensureTitle(mod), module_code: mod, parent_code:'', credits: derivedCr || 0, nqf_level: km.nqf_level || km.nqfLevel || 0, evidence_type:'knowledge', approved: false });
        (km.topics || []).forEach(t => {
          push({ node_type:'topic_kt', code: t.code, title: t.description || t.title || ensureTitle(t.code), module_code: mod, parent_code: mod, weight_pct: t.weight_pct || t.weightPercent || 0 });
          (t.elements || []).forEach(el => {
            const ec = typeof el === 'string' ? el : el?.code;
            if (!ec) return;
            push({ node_type:'topic_element', code: ec, title: (typeof el === 'object' ? (el.description || ec) : ec), module_code: mod, parent_code: t.code, text: (typeof el === 'object' ? el.description || '' : '') });
          });
          (t.assessments || []).forEach(ac => {
            const acCode = typeof ac === 'string' ? ac : ac?.code;
            if (!acCode) return;
            const acDesc = (typeof ac === 'object' ? ac.description || '' : '');
            push({ node_type:'assessment_criterion', code: acCode, title: acDesc || acCode, module_code: mod, parent_code: t.code, weight_pct: (typeof ac === 'object' ? (ac.weight_pct || ac.weightPercent || 0) : 0), text: acDesc, links:[{ rel:'assesses', to_code: t.code }] });
          });
        });
      });
      // PM
      (tree.PM || []).forEach(pm => {
        const mod = pm.code;
        const derivedCr = pm.credits || parseCreditsFromText(pm.description || pm.title || '');
        push({ node_type:'module_pm', code: mod, title: pm.description || pm.title || ensureTitle(mod), module_code: mod, parent_code:'', credits: derivedCr || 0, evidence_type:'practical', approved: false });
        (pm.subtasks || []).forEach(ps => {
          push({ node_type:'practical_skill', code: ps.code, title: ps.description || ps.title || ensureTitle(ps.code), module_code: mod, parent_code: mod, evidence_type:'practical', credits: ps.credits || 0, text: ps.description || '', approved: false });
          const paList = Array.isArray(ps.elements) ? ps.elements : (ps.actions || []);
          paList.forEach(pa => {
            const pac = typeof pa === 'string' ? pa : pa?.code;
            if (!pac) return;
            const paDesc = (typeof pa === 'object' ? pa.description || '' : '');
            push({ node_type:'metadata', code: pac, title: paDesc || pac, module_code: mod, parent_code: ps.code, text: paDesc, credits: (typeof pa === 'object' ? (pa.credits || 0) : 0) });
          });
        });
      });
      // WM
      (tree.WM || []).forEach(wm => {
        const mod = wm.code;
        const derivedCr = wm.credits || parseCreditsFromText(wm.description || wm.title || '');
        push({ node_type:'module_wm', code: mod, title: wm.description || wm.title || ensureTitle(mod), module_code: mod, parent_code:'', credits: derivedCr || 0, evidence_type:'workplace', approved: false });
        (wm.subtasks || []).forEach(we => {
          push({ node_type:'work_experience_item', code: we.code, title: we.description || we.title || ensureTitle(we.code), module_code: mod, parent_code: mod, evidence_type:'workplace', credits: we.credits || 0, text: we.description || '', approved: false });
          const waList = Array.isArray(we.elements) ? we.elements : (we.actions || []);
          waList.forEach(wa => {
            const wac = typeof wa === 'string' ? wa : wa?.code;
            if (!wac) return;
            const waDesc = (typeof wa === 'object' ? wa.description || '' : '');
            push({ node_type:'work_activity', code: wac, title: waDesc || wac, module_code: mod, parent_code: we.code, evidence_type:'workplace', text: waDesc, credits: (typeof wa === 'object' ? (wa.credits || 0) : 0) });
          });
        });
      });
      return out;
    };
    (async () => {
      try {
        setIsStreaming(true);
        // server will parse and persist using sanitized extractor and return full JSON
        const resp = await api.post(`/qualifications/${qualificationId}/curriculum/extract/new`);
        const payload = resp.data;
        const tree = payload?.files?.[0]?.tree;
        if (tree) {
          // Build nodes locally from returned tree to avoid extra GET
          const built = buildNodesFromLocalTree(tree);
          setNodes(built);
        } else {
          // Fallback: fetch nodes from DB if payload did not include files/tree
          const res2 = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
          if (Array.isArray(res2.data?.nodes)) setNodes(res2.data.nodes);
        }
      } catch (e) {
        setDebugEvents(prev => [...prev, { stage: 'local_extract_error', error: String(e) }]);
      } finally {
        setIsStreaming(false);
      }
    })();
  };

  const stopExtraction = () => {
    if (esRef.current) {
      try { esRef.current.close(); } catch {}
      esRef.current = null;
    }
    setIsStreaming(false);
  };

  const clearNodes = async () => {
    if (!qualificationId) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/qualifications/${qualificationId}/requirement-nodes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ nodes: [] })
      });
      setNodes([]);
    } catch {}
  };

  // No polling function needed anymore; SSE appends nodes live

  const fetchTargetCount = async () => {
    if (!qualificationId) return false;
    try {
      const res = await api.get(`/qualifications/${qualificationId}/requirement-nodes/count`);
      const data = res.data;
      if (res.status === 200 && data && data.target_nodes) {
        setTargetNodes(data.target_nodes);
        return true;
      }
      setCountError(data?.message || 'Failed to get count');
      return false;
    } catch (e) {
      setCountError('Failed to get count');
      return false;
    }
  };

  const startCount = async () => {
    setIsCounting(true);
    setCountError(null);
    try {
      await fetchTargetCount();
    } finally {
      setIsCounting(false);
    }
  };

  const rerunExtraction = async () => {
    stopExtraction();
    setIsComplete(false);
    await clearNodes();
    setTargetNodes(null);
    await fetchTargetCount();
    // After clearing nodes, immediately go to canvas for direct workflow
    try {
      if (typeof onOpenCanvas === 'function') onOpenCanvas();
    } catch {}
  };

  const fetchLocalTree = async () => {
    if (!qualificationId) return;
    try {
      const res = await api.get(`/qualifications/${qualificationId}/curriculum-tree/local?t=${Date.now()}`);
      setLocalTree(res.data?.tree || null);
    } catch {}
  };

  useEffect(() => () => stopExtraction(), []);

  // Do not auto-start extraction. User must start after count.

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Hours helpers
  const NOTIONAL_PER_CREDIT = 10;
  const ACTIVE_FACTOR = {
    knowledge_based: 0.55,
    practical_requirements: 0.65,
    workplace_experience: 0.20,
    assessment: 0
  };
  const toNotionalHours = (credits) => (Number(credits) || 0) * NOTIONAL_PER_CREDIT;
  const activeHoursForCategory = (category, creditsOrHours, isNotional = false) => {
    const factor = ACTIVE_FACTOR[category] || 0;
    const notional = isNotional ? (Number(creditsOrHours) || 0) : toNotionalHours(creditsOrHours);
    return Math.round(notional * factor);
  };
  // Normalizer used for WA mapping keys when only display code is available
  const normalizeWeDisplayCode = (code) => {
    if (!code) return '';
    const s = String(code).toUpperCase();
    let m = s.match(/\bWM-(\d{2})-WE?(\d{2})\b/);
    if (m) return `WM-${m[1]}-WE${m[2]}`;
    m = s.match(/\b\d{9}-WM-(\d{2})-(\d{2})\b/);
    if (m) return `WM-${m[1]}-WE${m[2]}`;
    return s;
  };
  const proportionalFromPct = (pct, moduleCredits, category) => {
    const p = Number(pct) || 0;
    const mc = Number(moduleCredits) || 0;
    const credits = +(mc * p / 100).toFixed(2);
    const notional = Math.round(credits * NOTIONAL_PER_CREDIT);
    const active = activeHoursForCategory(category, credits);
    return { credits, notional, active };
  };

  const renderModuleDetails = (module, category) => {
    const colorScheme = categoryColors[category];
    const moduleCredits = (() => {
      const base = Number(module.credits) || 0;
      if (base > 0) return base;
      if (category === 'practical_requirements') return derivePmModuleCredits(module);
      if (category === 'workplace_experience') return deriveWmModuleCredits(module);
      return 0;
    })();
    
    return (
      <Paper
        key={module.code || module.title}
        elevation={2}
        sx={{
          p: 3,
          mb: 2,
          border: `1px solid ${colorScheme.primary}20`,
          borderRadius: 3,
          backgroundColor: 'white',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 4,
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${colorScheme.primary}15`
          }
        }}
      >
        {/* Header Section with Visual Indicators */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          {/* Left side - Module info */}
          <Box flex={1}>
            {/* Module Type Badge */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: colorScheme.primary,
                  border: `2px solid ${colorScheme.light}`
                }}
              />
              <Chip
                label={categoryLabels[category]}
                size="small"
                sx={{
                  backgroundColor: colorScheme.light,
                  color: colorScheme.dark,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            {/* Module Title */}
            <Typography 
              variant="h6" 
              color={colorScheme.dark} 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 1
              }}
            >
              {cleanTitle(module.title)}
            </Typography>
            
            {/* Module Code */}
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontFamily: 'monospace',
                backgroundColor: 'grey.100',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mb: 1
              }}
            >
              {module.code || 'No Code'}
            </Typography>

            {/* KM Extras: number of KTs (if any) */}
            {category === 'knowledge_based' && Array.isArray(module.topics) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip size="small" variant="outlined" label={`KTs: ${module.topics.length}`} />
                {module.topics.length > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Σ weight: ${module.topics.reduce((s,t)=>s+(t.weight_pct||0),0).toFixed(0)}%`}
                  />
                )}
              </Box>
            )}
          </Box>
          
          {/* Right side - Credits and percentage */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end',
              gap: 1
            }}
          >
            {/* Module approval summary with progress */}
            {(() => {
              const items = category === 'knowledge_based'
                ? (Array.isArray(module.topics) ? module.topics : [])
                : category === 'practical_requirements'
                  ? (Array.isArray(module.practicalSkills) ? module.practicalSkills : [])
                  : category === 'workplace_experience'
                    ? (Array.isArray(module.workExperienceItems) ? module.workExperienceItems : [])
                    : [];
              const total = items.length;
              const approved = items.filter(i => !!i.approved).length;
              const allApproved = total > 0 && approved === total;
              return (
                <Chip
                  size="small"
                  color={allApproved ? 'success' : 'default'}
                  label={`Approved ${approved}/${total}`}
                />
              );
            })()}
            {/* Credits Badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: colorScheme.light,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${colorScheme.primary}30`
              }}
            >
              <Typography 
                variant="h5" 
                color={colorScheme.dark}
                sx={{ fontWeight: 700 }}
              >
                {module.credits || 0}
              </Typography>
              <Typography 
                variant="caption" 
                color={colorScheme.dark}
                sx={{ fontWeight: 500 }}
              >
                credits • {toNotionalHours(module.credits)} hrs notional • {activeHoursForCategory(category, module.credits)} hrs active
              </Typography>
            </Box>
            
            {/* Percentage */}
            {module.percentage && (
              <Chip
                label={`${module.percentage}%`}
                size="small"
                sx={{
                  backgroundColor: `${colorScheme.primary}15`,
                  color: colorScheme.dark,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Box>
        </Box>
        
        {/* Content Section */}
        <Box>
          {/* Topics (KT) Accordion for Knowledge Modules */}
          {category === 'knowledge_based' && module.topics && module.topics.length > 0 && (
            <Accordion sx={{ mb: 2, border: `1px solid ${colorScheme.primary}20`, boxShadow: 'none', width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorScheme.dark }}>
                    Topics (KTs) — {module.topics.length}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Total KT weight</Typography>
                    <Chip size="small" label={`${(module.topics.reduce((s,t)=>s + (t.weight_pct||0),0)).toFixed(0)}% • ${proportionalFromPct(module.topics.reduce((s,t)=>s + (t.weight_pct||0),0), moduleCredits, category).credits} cr • ${proportionalFromPct(module.topics.reduce((s,t)=>s + (t.weight_pct||0),0), moduleCredits, category).notional} hrs • ${proportionalFromPct(module.topics.reduce((s,t)=>s + (t.weight_pct||0),0), moduleCredits, category).active} hrs`} />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ width: '100%' }}>
                  {module.topics.map((topic, idx) => (
                    <Box key={topic.code || idx} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${colorScheme.primary}15`, backgroundColor: `${colorScheme.primary}05`, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorScheme.dark }}>
                          {topic.code ? `${topic.code} — ` : ''}{topic.title || 'Topic'}
                        </Typography>
                        <Chip size="small" variant="outlined" label={`${(topic.weight_pct||0)}% • ${proportionalFromPct((topic.weight_pct||0), moduleCredits, category).credits} cr • ${proportionalFromPct((topic.weight_pct||0), moduleCredits, category).notional} hrs • ${proportionalFromPct((topic.weight_pct||0), moduleCredits, category).active} hrs`} />
                        {module.topicElements && module.topicElements[topic.code] && (
                          <Chip size="small" label={`elements: ${module.topicElements[topic.code].length}`} />
                        )}
                        {/* Approve button moved to bottom-right of elements area */}
                      </Box>
                      {module.topicElements && module.topicElements[topic.code] && (
                        <Box sx={{ pl: 1, width: '100%', mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {module.topicElements[topic.code].map((el, eIdx) => (
                            <Chip
                              key={el.code || eIdx}
                              label={
                                <Box sx={{ maxWidth: '300px' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                    {el.code || ''}
                                  </Typography>
                                  {(el.title || el.description) && (
                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {el.title || el.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 0.5, mr: 0.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', height: 'auto', padding: '4px 8px' } }}
                            />
                          ))}
                        </Box>
                      )}

                      {/* Linked Assessments (IAC) */}
                      {module.assessmentsByTopic && module.assessmentsByTopic[topic.code] && (
                        <Box sx={{ pl: 1, mt: 0.5, width: '100%', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {module.assessmentsByTopic[topic.code].map((iac, iIdx) => (
                            <Chip
                              key={`${iac.code || 'IAC'}-${iIdx}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              label={
                                <Box sx={{ maxWidth: '300px' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                    {iac.code || 'IAC'}
                                  </Typography>
                                  {iac.title && (
                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {iac.title}
                                    </Typography>
                                  )}
                                  {iac.description && iac.description !== iac.title && (
                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: 'italic' }}>
                                      {iac.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ mb: 0.5, mr: 0.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', height: 'auto', padding: '4px 8px' } }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Practical Skills (PS) Accordion for Practical Requirements */}
          {category === 'practical_requirements' && Array.isArray(module.practicalSkills) && module.practicalSkills.length > 0 && (
            <Accordion sx={{ mb: 2, border: `1px solid ${colorScheme.primary}20`, boxShadow: 'none', width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorScheme.dark }}>
                    Practical Skills (PS) — {module.practicalSkills.length}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ width: '100%' }}>
                  {module.practicalSkills.map((ps, idx) => (
                    <Box key={ps.code || idx} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${colorScheme.primary}15`, backgroundColor: `${colorScheme.primary}05`, width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorScheme.dark, mb: 0.5 }}>
                        {ps.code ? `${ps.code} — ` : ''}{ps.title || 'Practical Skill'}
                      </Typography>
                      {ps.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {ps.description}
                        </Typography>
                      )}
                      {/* Applied Knowledge under PS */}
                      {module.appliedKnowledgeByPS && module.appliedKnowledgeByPS[ps.code] && (
                        <Box sx={{ pl: 1, mb: 1, position: 'relative' }}>
                          {module.appliedKnowledgeByPS[ps.code].map((ak, aIdx) => (
                            <Typography key={ak.code || aIdx} variant="body2" color="text.secondary">
                              {ak.code ? `${ak.code}: ` : ''}{ak.title || ak.description || ''}
                            </Typography>
                          ))}
                          <Chip
                            size="small"
                            sx={{ position: 'absolute', right: 8, bottom: 8 }}
                            color={ps.approved ? 'success' : 'default'}
                            label={ps.approved ? 'Approved' : 'Approve'}
                            onClick={async () => {
                              try {
                                await api.post(`/qualifications/${qualificationId}/requirement-nodes/approve`, { code: ps.code, approved: !ps.approved });
                                const res = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
                                if (Array.isArray(res.data?.nodes)) setNodes(res.data.nodes);
                              } catch {}
                            }}
                          />
                        </Box>
                      )}
                      {/* Practical Activities under PS */}
                      {module.practicalActivitiesByPS && module.practicalActivitiesByPS[ps.code] && (
                        <Box sx={{ pl: 1, mb: 1 }}>
                          {module.practicalActivitiesByPS[ps.code].map((pa, pIdx) => (
                            <Typography key={pa.code || pIdx} variant="body2" color="text.secondary">
                              {pa.code ? `${pa.code}: ` : ''}{pa.title || pa.description || ''}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {/* IACs linked to PS */}
                      {module.assessmentsByPS && module.assessmentsByPS[ps.code] && (
                        <Box sx={{ pl: 1, mt: 0.5, width: '100%' }}>
                          {module.assessmentsByPS[ps.code].map((iac, iIdx) => (
                            <Box key={`${iac.code || 'IAC'}-${iIdx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip size="small" color="secondary" variant="outlined" label={iac.code || 'IAC'} />
                              <Typography variant="body2" color="text.secondary">
                                {iac.title || ''}
                              </Typography>
                              {iac.weight_pct > 0 && (
                                <Chip size="small" label={`${iac.weight_pct}% • ${proportionalFromPct(iac.weight_pct, moduleCredits, category).credits} cr • ${proportionalFromPct(iac.weight_pct, moduleCredits, category).notional} hrs • ${proportionalFromPct(iac.weight_pct, moduleCredits, category).active} hrs`} />
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {/* Approve button moved to bottom-right of applied knowledge area */}
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Work Experience Items (WE) Accordion for Workplace Experience */}
          {category === 'workplace_experience' && Array.isArray(module.workExperienceItems) && module.workExperienceItems.length > 0 && (
            <Accordion sx={{ mb: 2, border: `1px solid ${colorScheme.primary}20`, boxShadow: 'none', width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorScheme.dark }}>
                    Workplace Experience (WE) — {module.workExperienceItems.length}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ width: '100%' }}>
                  {module.workExperienceItems.map((we, idx) => (
                    <Box key={we.code || idx} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: `1px solid ${colorScheme.primary}15`, backgroundColor: `${colorScheme.primary}05`, width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorScheme.dark, mb: 0.5 }}>
                        {we.code ? `${we.code} — ` : ''}{we.title || 'Work Experience Item'}
                      </Typography>
                      {we.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {we.description}
                        </Typography>
                      )}
                      {/* Work Activities (WA) under WE */}
                      {module.workActivitiesByWE && module.workActivitiesByWE[we.normCode || normalizeWeDisplayCode(we.code)] && (
                        <Box sx={{ pl: 1, mb: 1 }}>
                          {module.workActivitiesByWE[we.normCode || normalizeWeDisplayCode(we.code)].map((wa, wIdx) => (
                            <Typography key={wa.code || wIdx} variant="body2" color="text.secondary">
                              {wa.code ? `${wa.code}: ` : ''}{wa.title || wa.description || ''}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {we.credits && (
                        <Chip
                          label={`${we.credits} credits • ${toNotionalHours(we.credits)} hrs notional • ${activeHoursForCategory(category, we.credits)} hrs active`}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: colorScheme.primary,
                            color: colorScheme.primary,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {/* Subtasks Section */}
          {module.subtasks && module.subtasks.length > 0 && (
            <Box mt={3} sx={{ width: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: colorScheme.primary
                  }}
                />
                <Typography 
                  variant="subtitle1" 
                  color={colorScheme.dark}
                  sx={{ fontWeight: 600 }}
                >
                  Learning Objectives & Subtasks
                </Typography>
                <Chip
                  label={`${module.subtasks.length} subtasks`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              
              <Box sx={{ pl: 2, width: '100%' }}>
                {module.subtasks.map((subtask, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: 2,
                      p: 2,
                      backgroundColor: `${colorScheme.primary}05`,
                      borderRadius: 2,
                      border: `1px solid ${colorScheme.primary}10`,
                      width: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: colorScheme.primary,
                        mt: 0.5,
                        flexShrink: 0
                      }}
                    />
                    <Box flex={1}>
                      <Typography 
                        variant="subtitle2" 
                        color={colorScheme.dark}
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {subtask.title || subtask}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {subtask.description || subtask.requirement}
                      </Typography>
                      {subtask.credits && (
                        <Chip
                          label={`${subtask.credits} credits • ${toNotionalHours(subtask.credits)} hrs notional • ${activeHoursForCategory(category, subtask.credits)} hrs active`}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: colorScheme.primary,
                            color: colorScheme.primary,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Learning Outcomes Section */}
          {module.learning_outcomes && module.learning_outcomes.length > 0 && (
            <Box mt={3}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: colorScheme.dark
                  }}
                />
                <Typography 
                  variant="subtitle1" 
                  color={colorScheme.dark}
                  sx={{ fontWeight: 600 }}
                >
                  Learning Outcomes
                </Typography>
                <Chip
                  label={`${module.learning_outcomes.length} outcomes`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              
              <Box sx={{ pl: 2 }}>
                {module.learning_outcomes.map((outcome, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: 1.5,
                      p: 1.5,
                      backgroundColor: `${colorScheme.dark}05`,
                      borderRadius: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: colorScheme.dark,
                        mt: 0.5,
                        flexShrink: 0
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.primary"
                      sx={{ lineHeight: 1.4 }}
                    >
                      {outcome}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        
        {/* Footer Section - Additional Metadata */}
        <Box 
          sx={{ 
            mt: 3, 
            pt: 2, 
            borderTop: `1px solid ${colorScheme.primary}20`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box display="flex" gap={1}>
            <Chip
              label={`${categoryLabels[category]}`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: colorScheme.primary,
                color: colorScheme.primary,
                fontSize: '0.7rem'
              }}
            />
            {module.credits && (
              <Chip
                label={`${module.credits} credits • ${toNotionalHours(module.credits)} hrs notional • ${activeHoursForCategory(category, module.credits)} hrs active`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'grey.400',
                  color: 'text.secondary',
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Box>
          
          {/* Progress indicator or status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'success.main'
              }}
            />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
              Active Module
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderNewModuleDetails = (module, category) => {
    const colorScheme = categoryColors[category];
    
    // Calculate relative percentages based on module credits
    const moduleCredits = module.credits || 0;
    
    // Calculate topic credits (topic percentage of module credits)
    const calculateTopicCredits = (topicPercentWeight) => {
      if (!topicPercentWeight || !moduleCredits) return 0;
      return (topicPercentWeight / 100) * moduleCredits;
    };
    
    // Calculate element/IAC credits (element percentage of topic credits)
    const calculateElementCredits = (elementPercentWeight, topicCredits) => {
      if (!elementPercentWeight || !topicCredits) return 0;
      return (elementPercentWeight / 100) * topicCredits;
    };
    
    // Handle different data structures: KM modules have 'topics', PM/WM modules have 'subtasks'
    const topics = module.topics || module.subtasks || [];
    const moduleType = module.topics ? 'KM' : (module.subtasks ? 'PM/WM' : 'Unknown');
    
    return (
      <Paper
        key={module.module_code}
        elevation={2}
        sx={{
          p: 3,
          mb: 2,
          border: `1px solid ${colorScheme.primary}20`,
          borderRadius: 3,
          backgroundColor: 'white',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 4,
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${colorScheme.primary}15`
          }
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          {/* Left side - Module info */}
          <Box flex={1}>
            {/* Module Type Badge */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: colorScheme.primary,
                  border: `2px solid ${colorScheme.light}`
                }}
              />
              <Chip
                label={`${moduleType} Module`}
                size="small"
                sx={{
                  backgroundColor: colorScheme.light,
                  color: colorScheme.dark,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            {/* Module Title */}
            <Typography 
              variant="h6" 
              color={colorScheme.dark} 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 1
              }}
            >
              {cleanTitle(module.title)}
            </Typography>
            
            {/* Module Code */}
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontFamily: 'monospace',
                backgroundColor: 'grey.100',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mb: 1
              }}
            >
              {module.code} • NQF Level {module.nqf_level}
            </Typography>
          </Box>
          
          {/* Right side - Credits and status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {module.credits && (
              <Chip
                label={`${module.credits} credits • ${toNotionalHours(module.credits)} hrs notional • ${activeHoursForCategory(category, module.credits)} hrs active`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'grey.400',
                  color: 'text.secondary',
                  fontSize: '0.7rem'
                }}
              />
            )}
            
            {/* Status indicator */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'success.main'
              }}
            />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
              Active
            </Typography>
          </Box>
        </Box>
        
        {/* Topics breakdown with elements and IAC */}
        {topics && topics.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: colorScheme.dark }}>
              {moduleType === 'KM' ? 'Topics' : 'Practical Skills'} ({topics.length})
            </Typography>
            {topics.map((topic, topicIndex) => {
              // Calculate this topic's credits (percentage of module credits)
              const topicCredits = calculateTopicCredits(topic.weight_pct || topic.percent_weight);
              
              return (
              <Box key={topicIndex} sx={{ mb: 2, p: 2, border: `1px solid ${colorScheme.primary}20`, borderRadius: 2, backgroundColor: `${colorScheme.primary}05` }}>
                {/* Topic header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colorScheme.dark }}>
                    {topic.code}: {topic.title}
                  </Typography>
                  <Chip
                    label={`${topic.weight_pct || topic.percent_weight}% (${topicCredits.toFixed(1)} credits)`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: colorScheme.primary,
                      color: colorScheme.dark,
                      fontSize: '0.7rem'
                    }}
                  />
                </Box>
                
                {/* Elements */}
                {topic.elements && topic.elements.length > 0 && (
                  <Box sx={{ ml: 2, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      Elements ({topic.elements.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {topic.elements.map((element, elementIndex) => {
                        const elementCredits = calculateElementCredits(element.weight_pct || element.percent_weight, topicCredits);
                        return (
                        <Chip
                          key={elementIndex}
                          label={`${element.code}: ${element.title} (${element.weight_pct || element.percent_weight}% of topic = ${elementCredits.toFixed(2)} credits)`}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'grey.400',
                            color: 'text.secondary',
                            fontSize: '0.65rem'
                          }}
                        />
                      )})}
                    </Box>
                  </Box>
                )}
                
                {/* IAC Items */}
                {topic.iac && topic.iac.length > 0 && (
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      IAC Items ({topic.iac.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {topic.iac.map((iac, iacIndex) => {
                        const iacCredits = calculateElementCredits(iac.weight_pct || iac.percent_weight, topicCredits);
                        return (
                        <Chip
                          key={iacIndex}
                          label={`${iac.code}: ${iac.description} (${iac.weight_pct || iac.percent_weight}% of topic = ${iacCredits.toFixed(2)} credits)`}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'orange.400',
                            color: 'orange.700',
                            fontSize: '0.65rem'
                          }}
                        />
                      )})}
                    </Box>
                  </Box>
                )}
              </Box>
            )})}
          </Box>
        )}
      </Paper>
    );
  };

  const renderCategory = (category, modules, totalCredits) => {


    if (!modules || modules.length === 0) {

      return null;
    }
    
    const colorScheme = categoryColors[category];
    const IconComponent = categoryIcons[category];
    const isExpanded = expandedCategories[category];
    
    return (
      <Accordion
        key={category}
        expanded={isExpanded}
        onChange={() => toggleCategory(category)}
        sx={{
          mb: 2,
          '&:before': { display: 'none' },
          border: `1px solid ${colorScheme.primary}`,
          borderRadius: 2
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: colorScheme.light,
            borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: `${colorScheme.primary}10`
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            {/* Category Icon with Background */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: colorScheme.primary,
                color: 'white'
              }}
            >
              <IconComponent sx={{ fontSize: 24 }} />
            </Box>
            
            <Box flex={1}>
              <Typography 
                variant="h6" 
                color={colorScheme.dark}
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                {categoryLabels[category]}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: colorScheme.primary
                  }}
                />
                {modules.length} modules • {totalCredits} total credits • {toNotionalHours(totalCredits)} hrs notional • {activeHoursForCategory(category, toNotionalHours(totalCredits), true)} hrs active
              </Typography>
            </Box>
          </Box>
          
          {/* Right side indicators */}
          <Box display="flex" gap={1} alignItems="center">
            {/* Module count badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: colorScheme.primary,
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 700
              }}
            >
              {modules.length}
            </Box>
            
            {/* Credits badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                backgroundColor: 'white',
                borderRadius: 2,
                border: `2px solid ${colorScheme.primary}`,
                color: colorScheme.dark,
                fontWeight: 600
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {totalCredits}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                cr
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 2, backgroundColor: 'white' }}>
          <Grid container spacing={2}>
            {modules.map((module) => (
              <Grid size={{ xs: 12 }} key={module.module_code || module.code || module.title} sx={{ width: '100%' }}>
                <Box sx={{ width: '100%' }}>
                {hasNewData ? renderNewModuleDetails(module, category) : renderModuleDetails(module, category)}
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Build sections from new curriculum data or nodes if present (KM then PM then WM), full width items
  const hasNewData = newCurriculumData && (newCurriculumData.PM || newCurriculumData.WM || newCurriculumData.KM);
  const hasNodes = effectiveNodes && effectiveNodes.length > 0;
  let kmModules = [];
  let pmModules = [];
  let wmModules = [];
  
  if (hasNewData) {

    // Use new structured curriculum data
    kmModules = newCurriculumData.KM || [];
    pmModules = newCurriculumData.PM || [];
    wmModules = newCurriculumData.WM || [];

  } else if (hasNodes) {

    const byModuleCode = {};
    // Map KM number -> module code for reliable grouping when module_code is missing on topics/elements
    const kmNumberToModuleCode = {};
    const pmNumberToModuleCode = {};
    effectiveNodes.forEach(m => {
      if (m.node_type === 'module_km' && typeof m.code === 'string') {
        const match = m.code.match(/KM-(\d{2})/);
        if (match) kmNumberToModuleCode[match[1]] = m.code;
      }
      if (m.node_type === 'module_pm' && typeof m.code === 'string') {
        const match = m.code.match(/PM-(\d{2})/);
        if (match) pmNumberToModuleCode[match[1]] = m.code;
      }
    });

    const normalizeWeCode = (code) => {
      if (!code) return '';
      const s = String(code).toUpperCase();
      let m = s.match(/\bWM-(\d{2})-WE?(\d{2})\b/);
      if (m) return `WM-${m[1]}-WE${m[2]}`;
      m = s.match(/\b\d{9}-WM-(\d{2})-(\d{2})\b/);
      if (m) return `WM-${m[1]}-WE${m[2]}`;
      return s;
    };

    enrichedNodes.forEach(n => {
      let modCode = n.module_code || (n.node_type === 'module_km' || n.node_type === 'module_pm' || n.node_type === 'module_wm' ? n.code : null);

      // Derive module code for KTs and elements if missing
      if (!modCode && (n.node_type === 'topic_kt' || n.node_type === 'topic_element')) {
        let kmNum = null;
        if (typeof n.code === 'string') {
          const m1 = n.code.match(/KM-(\d{2})/);
          if (m1) kmNum = m1[1];
        }
        if (!kmNum && typeof n.parent_code === 'string') {
          const m2 = n.parent_code.match(/KM-(\d{2})/);
          if (m2) kmNum = m2[1];
        }
        if (kmNum && kmNumberToModuleCode[kmNum]) {
          modCode = kmNumberToModuleCode[kmNum];
        }
        // Fallback: group under KM-xx until the real module_km arrives
        if (!modCode && kmNum) {
          modCode = `KM-${kmNum}`;
        }
      }

      // Derive module code for IACs if missing, using parent/links or IAC number prefix
      if (!modCode && n.node_type === 'assessment_criterion') {
        let kmNum = null;
        if (typeof n.parent_code === 'string') {
          const m = n.parent_code.match(/KM-(\d{2})/);
          if (m) kmNum = m[1];
        }
        if (!kmNum && Array.isArray(n.links)) {
          for (const l of n.links) {
            if (l && typeof l.to_code === 'string') {
              const m = l.to_code.match(/KM-(\d{2})/);
              if (m) { kmNum = m[1]; break; }
            }
          }
        }
        if (!kmNum && typeof n.code === 'string') {
          const m = n.code.match(/IAC(\d{2})/i);
          if (m) kmNum = m[1];
        }
        if (kmNum && kmNumberToModuleCode[kmNum]) {
          modCode = kmNumberToModuleCode[kmNum];
        }
        if (!modCode && kmNum) {
          modCode = `KM-${kmNum}`;
        }
      }

      if (!modCode) {
        return;
      }
      if (!byModuleCode[modCode]) {
        byModuleCode[modCode] = { 
        code: modCode, 
        title: n.title || modCode, 
        credits: n.credits || 0, 
        percentage: n.weight_pct || null, 
        subtasks: [], 
        topics: [], 
        topicElements: {}, 
        assessmentsByTopic: {},
        practicalSkills: [],
        appliedKnowledgeByPS: {},
        practicalActivitiesByPS: {},
        workExperienceItems: [],
        workActivitiesByWE: {},
        approved: false
        };
      }
      if (n.node_type === 'module_km' || n.node_type === 'module_pm' || n.node_type === 'module_wm') {
        byModuleCode[modCode].title = n.title || byModuleCode[modCode].title;
        if (typeof n.credits === 'number') byModuleCode[modCode].credits = n.credits;
        if (typeof n.approved === 'boolean') byModuleCode[modCode].approved = n.approved;
      } else if (n.node_type === 'topic_kt') {
        // derive weight if missing by parsing percentage from title/text
        const derivedWeight = (() => {
          const s = `${n.title || ''} ${n.text || ''}`;
          const m = s.match(/(\d{1,3})\s*%/);
          return m ? parseInt(m[1], 10) : 0;
        })();
        const weight = (typeof n.weight_pct === 'number' && n.weight_pct > 0) ? n.weight_pct : derivedWeight;
        const tCredits = (typeof n.credits === 'number' && Number.isFinite(n.credits)) ? n.credits : 0;
        byModuleCode[modCode].topics.push({ code: n.code, title: n.title, description: n.text, weight_pct: weight, credits: tCredits, approved: !!n.approved });
      } else if (n.node_type === 'topic_element') {
        const p = n.parent_code || 'unknown';
        if (!byModuleCode[modCode].topicElements[p]) byModuleCode[modCode].topicElements[p] = [];
        byModuleCode[modCode].topicElements[p].push({ code: n.code, title: n.title, description: n.text });
      } else if (n.node_type === 'assessment_criterion') {
        // attach IACs to their KT via parent_code, or via links[].to_code where rel==='assesses'
        let ktCode = '';
        if (typeof n.parent_code === 'string' && /KM-\d{2}-KT\d{2}/.test(n.parent_code)) ktCode = n.parent_code;
        if (!ktCode && Array.isArray(n.links)) {
          const link = n.links.find(l => l && l.rel && l.to_code && l.rel.toLowerCase() === 'assesses' && /KM-\d{2}-KT\d{2}/.test(l.to_code));
          if (link) ktCode = link.to_code;
        }
        // Heuristic: map IACnnnn to KTxx within the same module if still unknown
        if (!ktCode && typeof n.code === 'string') {
          const m = n.code.match(/IAC(\d{2})/i);
          if (m) {
            const ktNum = m[1];
            const candidate = (byModuleCode[modCode]?.topics || []).find(t => typeof t.code === 'string' && new RegExp(`KT${ktNum}$`).test(t.code));
            if (candidate && candidate.code) ktCode = candidate.code;
          }
        }
        // Also map to PS when applicable
        let psCode = '';
        if (typeof n.parent_code === 'string' && /PM-\d{2}-PS\d{2}/.test(n.parent_code)) psCode = n.parent_code;
        if (!psCode && Array.isArray(n.links)) {
          const l2 = n.links.find(l => l && l.to_code && /PM-\d{2}-PS\d{2}/.test(l.to_code));
          if (l2) psCode = l2.to_code;
        }
        if (ktCode) {
          if (!byModuleCode[modCode].assessmentsByTopic[ktCode]) byModuleCode[modCode].assessmentsByTopic[ktCode] = [];
          const iacWeight = (typeof n.weight_pct === 'number' && n.weight_pct > 0) ? n.weight_pct : 0;
          byModuleCode[modCode].assessmentsByTopic[ktCode].push({ code: n.code, title: n.title, description: n.text, weight_pct: iacWeight });
        } else {
        }
        if (psCode) {
          if (!byModuleCode[modCode].assessmentsByPS) byModuleCode[modCode].assessmentsByPS = {};
          if (!byModuleCode[modCode].assessmentsByPS[psCode]) byModuleCode[modCode].assessmentsByPS[psCode] = [];
          const iacWeight = (typeof n.weight_pct === 'number' && n.weight_pct > 0) ? n.weight_pct : 0;
          byModuleCode[modCode].assessmentsByPS[psCode].push({ code: n.code, title: n.title, description: n.text, weight_pct: iacWeight });
        } else if (!ktCode) {
          byModuleCode[modCode].subtasks.push({ title: n.title || n.code, description: n.text || '', code: n.code, credits: n.credits });
        }
      } else if (n.node_type === 'practical_skill') {
        // PM practical skills as their own list with descriptions
        byModuleCode[modCode].practicalSkills.push({ code: n.code, title: n.title, description: n.text, credits: n.credits, approved: !!n.approved });
      } else if (n.node_type === 'applied_knowledge') {
        // Attach applied knowledge under its PS parent if available
        const psParent = typeof n.parent_code === 'string' && /PM-\d{2}-PS\d{2}/.test(n.parent_code) ? n.parent_code : '';
        if (psParent) {
          if (!byModuleCode[modCode].appliedKnowledgeByPS) byModuleCode[modCode].appliedKnowledgeByPS = {};
          if (!byModuleCode[modCode].appliedKnowledgeByPS[psParent]) byModuleCode[modCode].appliedKnowledgeByPS[psParent] = [];
          byModuleCode[modCode].appliedKnowledgeByPS[psParent].push({ code: n.code, title: n.title, description: n.text });
        }
      } else if (n.node_type === 'work_experience_item') {
        // Preserve original document code for display, and store normalized code for WA mapping
        const displayCode = n.code;
        const norm = normalizeWeCode(n.code);
        byModuleCode[modCode].workExperienceItems.push({ code: displayCode, normCode: norm, title: n.title, description: n.text, credits: n.credits, approved: !!n.approved });
      } else if (n.node_type === 'metadata' && typeof n.code === 'string' && /^PA\d{4}$/i.test(n.code)) {
        // Practical activities (PA) under PS
        const psParent = typeof n.parent_code === 'string' && /PM-\d{2}-PS\d{2}/.test(n.parent_code) ? n.parent_code : '';
        if (psParent) {
          if (!byModuleCode[modCode].practicalActivitiesByPS) byModuleCode[modCode].practicalActivitiesByPS = {};
          if (!byModuleCode[modCode].practicalActivitiesByPS[psParent]) byModuleCode[modCode].practicalActivitiesByPS[psParent] = [];
          byModuleCode[modCode].practicalActivitiesByPS[psParent].push({ code: n.code, title: n.title, description: n.text });
        }
      } else if (n.node_type === 'work_activity' && typeof n.code === 'string' && /^WA\d{4}$/i.test(n.code)) {
        // Work activities (WA) under WE
        const parentNorm = normalizeWeCode(n.parent_code);
        const weParent = typeof parentNorm === 'string' && /WM-\d{2}-WE\d{2}/.test(parentNorm) ? parentNorm : '';
        if (weParent) {
          if (!byModuleCode[modCode].workActivitiesByWE) byModuleCode[modCode].workActivitiesByWE = {};
          if (!byModuleCode[modCode].workActivitiesByWE[weParent]) byModuleCode[modCode].workActivitiesByWE[weParent] = [];
          byModuleCode[modCode].workActivitiesByWE[weParent].push({ code: n.code, title: n.title, description: n.text });
        }
      } else {
        byModuleCode[modCode].subtasks.push({ title: n.title || n.code, description: n.text || '', code: n.code, credits: n.credits, approved: !!n.approved });
      }
    });
    // Merge any temporary KM-xx groups into their real module codes once available
    Object.keys(kmNumberToModuleCode).forEach(kmNum => {
      const tempKey = `KM-${kmNum}`;
      const realKey = kmNumberToModuleCode[kmNum];
      if (byModuleCode[tempKey] && byModuleCode[realKey] && tempKey !== realKey) {
        const src = byModuleCode[tempKey];
        const dst = byModuleCode[realKey];
        dst.topics.push(...src.topics);
        Object.entries(src.topicElements).forEach(([k, v]) => {
          if (!dst.topicElements[k]) dst.topicElements[k] = [];
          dst.topicElements[k].push(...v);
        });
        Object.entries(src.assessmentsByTopic || {}).forEach(([k, v]) => {
          if (!dst.assessmentsByTopic[k]) dst.assessmentsByTopic[k] = [];
          dst.assessmentsByTopic[k].push(...v);
        });
        dst.subtasks.push(...src.subtasks);
        delete byModuleCode[tempKey];
      } else if (byModuleCode[tempKey] && !byModuleCode[realKey]) {
      } else if (tempKey === realKey) {
      }
    });

    
    // Check if there's any data in byModuleCode

    Object.keys(byModuleCode).forEach(key => {
    });

      const modulesArr = Object.values(byModuleCode).map(m => {
      const uniqBy = (arr, keyFn) => {
        const map = new Map();
        (arr || []).forEach(item => { const k = keyFn(item); if (!map.has(k)) map.set(k, item); });
        return Array.from(map.values());
      };
      m.topics = uniqBy(m.topics || [], it => it.code || it.title || JSON.stringify(it));
      Object.keys(m.topicElements || {}).forEach(k => {
        m.topicElements[k] = uniqBy(m.topicElements[k] || [], it => it.code || it.title || JSON.stringify(it));
      });
      Object.keys(m.assessmentsByTopic || {}).forEach(k => {
        m.assessmentsByTopic[k] = uniqBy(m.assessmentsByTopic[k] || [], it => it.code || it.title || JSON.stringify(it));
      });
      
      if (m.assessmentsByPS) {
        Object.keys(m.assessmentsByPS).forEach(k => {
          m.assessmentsByPS[k] = uniqBy(m.assessmentsByPS[k] || [], it => it.code || it.title || JSON.stringify(it));
        });
      }
      m.practicalSkills = uniqBy(m.practicalSkills || [], it => it.code || it.title || JSON.stringify(it));
      if (m.appliedKnowledgeByPS) {
        Object.keys(m.appliedKnowledgeByPS).forEach(k => {
          m.appliedKnowledgeByPS[k] = uniqBy(m.appliedKnowledgeByPS[k] || [], it => it.code || it.title || JSON.stringify(it));
        });
      }
      if (m.practicalActivitiesByPS) {
        Object.keys(m.practicalActivitiesByPS).forEach(k => {
          m.practicalActivitiesByPS[k] = uniqBy(m.practicalActivitiesByPS[k] || [], it => it.code || it.title || JSON.stringify(it));
        });
      }
      if (m.workActivitiesByWE) {
        Object.keys(m.workActivitiesByWE).forEach(k => {
          m.workActivitiesByWE[k] = uniqBy(m.workActivitiesByWE[k] || [], it => it.code || it.title || JSON.stringify(it));
        });
      }
      m.workExperienceItems = uniqBy(m.workExperienceItems || [], it => it.code || it.title || JSON.stringify(it));
      m.subtasks = uniqBy(m.subtasks || [], it => it.code || it.title || JSON.stringify(it));
      return m;
    });
    kmModules = modulesArr.filter(m => /KM-\d{2}|-KM-/.test(m.code));
    pmModules = modulesArr.filter(m => /PM-\d{2}|-PM-/.test(m.code));
    wmModules = modulesArr.filter(m => /WM-\d{2}|-WM-/.test(m.code));

  }

  // Fallback: if no KM modules were constructed, build a simple grouped view from parent/child relations
  if (hasNodes && kmModules.length === 0) {
    const modules = effectiveNodes.filter(n => n && n.node_type === 'module_km');
    kmModules = modules.map(m => {
      const topics = effectiveNodes.filter(t => t && t.node_type === 'topic_kt' && (t.parent_code === m.code || t.module_code === m.code)).map(t => ({
        code: t.code,
        title: t.title,
        description: t.text,
        weight_pct: (typeof t.weight_pct === 'number' ? t.weight_pct : 0),
        credits: (typeof t.credits === 'number' ? t.credits : 0),
        approved: !!t.approved
      }));
      return {
        code: m.code,
        title: m.title || m.code,
        credits: (typeof m.credits === 'number' ? m.credits : 0),
        topics,
        topicElements: {},
        assessmentsByTopic: {},
        subtasks: [],
        approved: !!m.approved
      };
    });
  }

  // Compute totals safely (support new data, live nodes, or fallback summary)
  // Derive robust totals: prefer module credits; if missing, fall back to summing child credits
  const safeSum = (arr) => (arr || []).reduce((t, v) => t + (Number(v) || 0), 0);
  const derivePmModuleCredits = (m) => {
    const moduleCredits = Number(m.credits) || 0;
    if (moduleCredits > 0) return moduleCredits;
    const psCredits = safeSum((m.practicalSkills || []).map(ps => ps.credits));
    const subtaskCredits = safeSum((m.subtasks || []).map(st => st.credits));
    return psCredits || subtaskCredits || 0;
  };
  const deriveWmModuleCredits = (m) => {
    const moduleCredits = Number(m.credits) || 0;
    if (moduleCredits > 0) return moduleCredits;
    const subtaskCredits = safeSum((m.subtasks || []).map(st => st.credits));
    return subtaskCredits || 0;
  };
  
  // Sum from displayed modules first; API summary row is often stale on production (50/45/30).
  const sumKmFromModules = hasNewData
    ? kmModules.reduce((s, m) => s + (Number(m.credits) || 0), 0)
    : hasNodes
    ? kmModules.reduce((s, m) => {
        const base = Number(m.credits) || 0;
        if (base > 0) return s + base;
        const sumTopics = (m.topics || []).reduce((t, tp) => t + (Number(tp.credits) || 0), 0);
        return s + sumTopics;
      }, 0)
    : 0;
  const sumPmFromModules = hasNewData
    ? pmModules.reduce((s, m) => s + (Number(m.credits) || 0), 0)
    : hasNodes
    ? pmModules.reduce((s, m) => s + derivePmModuleCredits(m), 0)
    : 0;
  const sumWmFromModules = hasNewData
    ? wmModules.reduce((s, m) => s + (Number(m.credits) || 0), 0)
    : hasNodes
    ? wmModules.reduce((s, m) => s + deriveWmModuleCredits(m), 0)
    : 0;

  const summaryTotals = newCurriculumSummary || lastExtractSummary || curriculumSummary?.summary;
  const persistedKm = Number(summaryTotals?.knowledge_based_total);
  const persistedPm = Number(summaryTotals?.practical_requirements_total);
  const persistedWm = Number(summaryTotals?.workplace_experience_total);
  const persistedHasCredits = persistedKm > 0 || persistedPm > 0 || persistedWm > 0;

  const knowledgeTotal =
    persistedHasCredits && !Number.isNaN(persistedKm)
      ? persistedKm
      : sumKmFromModules;

  const practicalTotal =
    persistedHasCredits && !Number.isNaN(persistedPm)
      ? persistedPm
      : sumPmFromModules;

  const workExpTotal =
    persistedHasCredits && !Number.isNaN(persistedWm)
      ? persistedWm
      : sumWmFromModules;
    
  const assessmentModules = curriculumSummary?.breakdown?.assessment || [];
  const assessmentTotal = hasNewData || hasNodes
    ? 0
    : (curriculumSummary?.summary?.assessment_total || 0);
    
  const totalCredits = hasNewData || hasNodes
    ? Number(knowledgeTotal || 0) + Number(practicalTotal || 0) + Number(workExpTotal || 0) + Number(assessmentTotal || 0)
    : Number(curriculumSummary?.total_credits || 0);

  // Notional and active hours aggregates
  const knowledgeNotional = toNotionalHours(knowledgeTotal);
  const practicalNotional = toNotionalHours(practicalTotal);
  const workExpNotional = toNotionalHours(workExpTotal);
  const totalNotional = toNotionalHours(totalCredits);
  const knowledgeActive = activeHoursForCategory('knowledge_based', knowledgeTotal);
  const practicalActive = activeHoursForCategory('practical_requirements', practicalTotal);
  const workExpActive = activeHoursForCategory('workplace_experience', workExpTotal);
  const totalActive = knowledgeActive + practicalActive + workExpActive;

  // Completion watcher: stop SSE and polling once credits target met
  useEffect(() => {
    if (targetCredits > 0 && totalCredits >= targetCredits && !isComplete) {
      setIsComplete(true);
      stopExtraction();
    }
  }, [targetCredits, totalCredits, isComplete]);

  return (
    <Box sx={{ mt: 0 }}>
      <Box mb={0} sx={{ position: 'sticky', top: 0, zIndex: 1200, background: '#fff', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', p: 1 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Curriculum Review" />
          <Tab label="Detailed Breakdown" />
          {/* ELO Canvas tab hidden per request */}
        </Tabs>
        <Box sx={{ ml: 'auto' }}>
          {/* Debug button hidden per request */}
        </Box>
      </Box>
      {tabIndex === 1 && null}
      {/* Removed mini summary cards to avoid duplication with Curriculum Review section below */}
        
        {/* Large detailed summary cards moved to Detailed Breakdown tab */}
      {tabIndex === 1 && (
        <Box ref={splitContainerRef} sx={{ display: 'flex', height: 'calc(100vh - 160px)', minHeight: 400 }}>
          {/* Left pane: breakdown list */}
          <Box sx={{ flex: `0 0 ${splitPos}%`, minWidth: 240, overflow: 'auto', pr: 1 }}>
            <Typography variant="h5" gutterBottom>Detailed Module Breakdown</Typography>
        {renderCategory(
          'knowledge_based',
              hasNewData ? kmModules : hasNodes ? kmModules : (curriculumSummary?.breakdown?.knowledge_based || []),
              knowledgeTotal
        )}
        {renderCategory(
          'practical_requirements',
              hasNewData ? pmModules : hasNodes ? pmModules : (curriculumSummary?.breakdown?.practical_requirements || []),
              practicalTotal
        )}
        {renderCategory(
          'workplace_experience',
              hasNewData ? wmModules : hasNodes ? wmModules : (curriculumSummary?.breakdown?.workplace_experience || []),
              workExpTotal
            )}
            {renderCategory('assessment', assessmentModules, assessmentTotal)}
          </Box>

          {/* Drag handle */}
          <Box
            onMouseDown={(e) => {
              const startX = e.clientX;
              const start = splitPos;
              const onMove = (ev) => {
                const delta = ev.clientX - startX;
                const container = splitContainerRef.current;
                const width = container && container.clientWidth ? container.clientWidth : window.innerWidth;
                const pctDelta = (delta / Math.max(1, width)) * 100;
                const next = Math.min(80, Math.max(20, start + pctDelta));
                setSplitPos(next);
              };
              const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            sx={{ width: 6, cursor: 'col-resize', background: '#e0e0e0', '&:hover': { background: '#ccc' } }}
          />

          {/* Right pane: source document */}
          <Box sx={{ flex: `1 1 ${100 - splitPos}%`, overflow: 'hidden', pl: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1">Source Curriculum Document</Typography>
              <Button
                size="small"
                variant="outlined"
                href={`${API_BASE_URL}/qualifications/${qualificationId}/curriculum/file`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<SchoolIcon />}
              >
                Open in New Tab
              </Button>
            </Box>
            <Box sx={{ flex: 1, border: '1px solid #e0e6ed', borderRadius: 1, overflow: 'hidden' }}>
              <object
                data={`${API_BASE_URL}/qualifications/${qualificationId}/curriculum/file#view=FitH`}
                type="application/pdf"
                style={{ width: '100%', height: '100%' }}
              >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    PDF preview unavailable in this browser.
                  </Typography>
                  <Button
                    variant="contained"
                    href={`${API_BASE_URL}/qualifications/${qualificationId}/curriculum/file`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<SchoolIcon />}
                    sx={{ mt: 2 }}
                  >
                    Open PDF in New Tab
                  </Button>
                </Box>
              </object>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Curriculum Review – Total Credits Summary with Progress (show in Review tab) */}
      {tabIndex === 0 && (
      <Box 
        mt={4} 
        p={4} 
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: 3,
          border: '1px solid #e0e6ed'
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>Curriculum Review</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Complete breakdown of all qualification components and credit requirements
          </Typography>
          
          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
            {!hasNewData && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              startIcon={isRegenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{ minWidth: 140 }}
            >
              {isRegenerating ? 'Generating...' : 'Generate'}
            </Button>
            )}
            
            {extractionIssues.length > 0 && (
              <Button
                variant="outlined"
                color={extractionIssues.some(issue => issue.severity === 'ERROR') ? 'error' : 'warning'}
                onClick={() => setIssuesDialogOpen(true)}
                startIcon={<WarningIcon />}
                sx={{ minWidth: 120 }}
              >
                {extractionIssues.length} Issue{extractionIssues.length !== 1 ? 's' : ''}
              </Button>
            )}
            
            {extractionIssues.length === 0 && (
              <Button
                variant="outlined"
                color="success"
                disabled
                startIcon={<CheckCircleIcon />}
                sx={{ minWidth: 120 }}
              >
                No Issues
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Progress Bar */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Total Progress
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              {totalCredits} / {totalCredits} credits
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 12,
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 6,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%)',
                borderRadius: 6,
                transition: 'width 0.5s ease-in-out'
              }}
            />
          </Box>
        </Box>
        
        {/* Credit Distribution */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e6ed'
              }}
            >
              <Typography variant="h6" color={categoryColors.knowledge_based.dark} sx={{ fontWeight: 700 }}>
                {knowledgeTotal}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Knowledge Credits • {knowledgeNotional} hrs notional • {knowledgeActive} hrs active
              </Typography>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e6ed'
              }}
            >
              <Typography variant="h6" color={categoryColors.practical_requirements.dark} sx={{ fontWeight: 700 }}>
                {practicalTotal}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Practical Credits • {practicalNotional} hrs notional • {practicalActive} hrs active
              </Typography>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e6ed'
              }}
            >
              <Typography variant="h6" color={categoryColors.workplace_experience.dark} sx={{ fontWeight: 700 }}>
                {workExpTotal}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Workplace Credits • {workExpNotional} hrs notional • {workExpActive} hrs active
              </Typography>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e6ed'
              }}
            >
              <Typography variant="h6" color={categoryColors.assessment.dark} sx={{ fontWeight: 700 }}>
                {totalCredits}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Credits • {totalNotional} hrs notional • {totalActive} hrs active
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {/* Total Summary */}
        <Box 
          mt={3} 
          p={3} 
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            border: '2px solid #1976d2',
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
            Total Qualification Credits: {totalCredits} • {totalNotional} hrs notional • {totalActive} hrs active
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This qualification requires completion of all modules across knowledge, practical, workplace experience, and assessment categories.
          </Typography>
        </Box>
      </Box>
      )}

      {/* Issues Dialog */}
      <Dialog open={issuesDialogOpen} onClose={() => setIssuesDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Extraction Issues ({extractionIssues.length})</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {/* Code Statistics Section */}
            {codeStatistics && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  Code Analysis Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                      {codeStatistics.uniqueCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unique Codes
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="h4" color="secondary" sx={{ fontWeight: 'bold' }}>
                      {codeStatistics.totalCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Occurrences
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="h4" color={codeStatistics.duplicateCount > 0 ? 'warning.main' : 'success.main'} sx={{ fontWeight: 'bold' }}>
                      {codeStatistics.duplicateCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duplicates
                    </Typography>
                  </Box>
                </Box>
                
                {/* Code Type Breakdown */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Code Type Breakdown:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${codeStatistics.codeTypes.modules} Modules`} size="small" color="primary" variant="outlined" />
                    <Chip label={`${codeStatistics.codeTypes.topics} Topics`} size="small" color="secondary" variant="outlined" />
                    <Chip label={`${codeStatistics.codeTypes.elements} Elements`} size="small" color="info" variant="outlined" />
                    <Chip label={`${codeStatistics.codeTypes.iacs} IACs`} size="small" color="success" variant="outlined" />
                  </Box>
                </Box>

                {/* Duplicate Codes */}
                {codeStatistics.duplicates && codeStatistics.duplicates.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'warning.main' }}>
                      Duplicate Codes Found:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {codeStatistics.duplicates.map((dup, index) => (
                        <Chip 
                          key={index}
                          label={`${dup.code} (${dup.count}x)`} 
                          size="small" 
                          color="warning" 
                          variant="filled"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Issues Section */}
            {extractionIssues.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" color="success.main">No Issues Found</Typography>
                <Typography variant="body2" color="text.secondary">
                  The curriculum extraction completed successfully with no validation issues.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  Extraction Issues ({extractionIssues.length})
                </Typography>
                <List>
                  {extractionIssues.map((issue, index) => (
                    <ListItem key={index} sx={{ borderBottom: '1px solid #eee' }}>
                      <ListItemIcon>
                        {issue.severity === 'ERROR' ? (
                          <WarningIcon color="error" />
                        ) : (
                          <WarningIcon color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={issue.severity} 
                              size="small" 
                              color={issue.severity === 'ERROR' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                            {issue.node && (
                              <Chip 
                                label={issue.node} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                              />
                            )}
                            {issue.parent && (
                              <Chip 
                                label={`parent: ${issue.parent}`}
                                size="small" 
                                variant="outlined"
                                color="default"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {issue.message}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Debug Modal to show localTree/actions */}
      <Dialog open={showDebug && debugOpen} onClose={() => setDebugOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Debug: Actions and Local Tree</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button onClick={startCount} variant="outlined" size="small" disabled={isCounting}>{isCounting ? 'Counting…' : 'Start count'}</Button>
            <Button onClick={fetchTargetCount} variant="text" size="small" disabled={isCounting}>Refresh count</Button>
            <Button onClick={fetchLocalTree} variant="text" size="small">Preview local tree</Button>
            <Button onClick={startExtraction} variant="contained" size="small" color="primary" disabled={isStreaming || !targetNodes}>Start Extraction</Button>
            <Button onClick={rerunExtraction} variant="outlined" size="small" color="primary" disabled={isStreaming}>Clear Nodes</Button>
            {typeof onDebugGoCanvas === 'function' && (
              <Button onClick={() => { try { setDebugOpen(false); } catch {} onDebugGoCanvas(); }} variant="contained" size="small" color="secondary">
                Generate ELO Canvas
              </Button>
            )}
            {typeof onDebugRegenerateELOs === 'function' && (
              <Button onClick={() => { try { setDebugOpen(false); } catch {} onDebugRegenerateELOs(); }} variant="outlined" size="small" color="warning">
                Regenerate ELOs (from scratch)
              </Button>
            )}
            {isStreaming && (
              <IconButton onClick={stopExtraction} size="small" color="warning" title="Stop Extraction">
                <CollapseIcon />
              </IconButton>
            )}
            <Chip label={isComplete ? 'Completed' : (isStreaming ? 'Extraction running' : 'Idle')} size="small" color={isComplete ? 'success' : (isStreaming ? 'success' : 'default')} />
            {targetNodes && (<Chip color="secondary" size="small" label={`Target nodes: ${targetNodes.total}`} />)}
          </Box>
          {localTree && (
            <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto', p: 1 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {JSON.stringify(localTree, null, 2)}
              </pre>
            </Paper>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
